import { ComponentChildren, h } from "preact";
import { useEffect, useLayoutEffect, useRef } from "preact/hooks";
import { Keyboard } from "../../../foundation/enums";
import classname from "../../../foundation/utils/classname";

import styles from "./DropdownOptions.module.css";

const EDGE_OFFSET = 12;

interface OptionsProps {
    class?: string;
    /** `DropdownOption` top position with respect to the `Dropdown`. */
    top?: number;
    /** `DropdownOption` left position with respect to the `Dropdown`. */
    left?: number;
    /** Whether to use given top value even if content does not fit vertically. Default: `false` */
    fixedTop?: boolean;
    /** Whether to limit height and allow scroll when content does not fit vertically. Default: `false` */
    autoScroll?: boolean;
    /** Whether to center DropdownOptions horizontally */
    centerX?: boolean;
    /** Dropdown option alignment option */
    alignRight?: boolean;
    /** Dropdown option facing option */
    faceRight?: boolean;
    /** To override default ESC button action. Default: `false` */
    disableClosingWithESC?: boolean;
    /** Whether this DropdownOptions is a subOptions inside another DropdownOptions */
    subOptions?: boolean;
    /** Keep the dropdown menu open after selection */
    keepOpenAfterSelection?: boolean;
    /** Suboptions y-alignment with parent item */
    alignSuboptionsToBottom?: boolean;
}

interface Props extends OptionsProps {
    parentRef: HTMLDivElement;
    children: ComponentChildren;
    onClose?: () => void;
    onClick?: () => void;
}

function DropdownOptions({
    class: cssClass,
    top,
    left,
    fixedTop = false,
    autoScroll = false,
    centerX,
    alignRight,
    faceRight,
    alignSuboptionsToBottom,
    parentRef,
    disableClosingWithESC = false,
    subOptions,
    children,
    onClose,
    onClick
}: Props) {
    const optionsRef = useRef<HTMLDivElement>(null);

    function positionOptions() {
        if (subOptions) {
            positionSubOptions();
            return;
        }

        const dropdownRect = parentRef.getBoundingClientRect();
        const optionsRect = optionsRef.current.getBoundingClientRect();
        const displacementY = typeof top === "number" ? top : dropdownRect.height;
        const displacementX = left || 0;

        let posY = dropdownRect.top + displacementY;
        let posX = dropdownRect.left;

        if (centerX) {
            posX += (dropdownRect.width - optionsRect.width) / 2;
        } else if (alignRight) {
            posX = dropdownRect.right - optionsRect.width;
        } else if (faceRight) {
            posX = dropdownRect.right;
        } else {
            posX += displacementX;
        }

        if (!fixedTop && posY < EDGE_OFFSET) {
            posY = EDGE_OFFSET;
        } else if (!fixedTop && (posY + optionsRect.height + EDGE_OFFSET > window.innerHeight)) {
            posY = window.innerHeight - optionsRect.height - EDGE_OFFSET;
            if (autoScroll && posY < EDGE_OFFSET) {
                posY = EDGE_OFFSET;
            }
        }

        if (autoScroll) {
            optionsRef.current.style.maxHeight = `${window.innerHeight - posY - EDGE_OFFSET}px`;
            optionsRef.current.style.overflowY = "auto";
        }

        if (posX < EDGE_OFFSET) {
            posX = EDGE_OFFSET;
        } else if (posX + optionsRect.width + EDGE_OFFSET > window.innerWidth) {
            posX = window.innerWidth - optionsRect.width - EDGE_OFFSET;
        }

        optionsRef.current.style.transform = `translate3d(${posX}px, ${posY}px, 0)`;
    }

    function positionSubOptions() {
        const parentOptionRect = parentRef.getBoundingClientRect();
        const optionsRect = optionsRef.current.getBoundingClientRect();

        const offsetX = left ?? 0;
        const toRight = parentOptionRect.right + optionsRect.width + EDGE_OFFSET + offsetX < window.innerWidth;
        const posX = toRight ? parentOptionRect.right + offsetX : parentOptionRect.left - optionsRect.width - offsetX;

        let posY = alignSuboptionsToBottom ? (parentOptionRect.bottom - optionsRect.height) : parentOptionRect.top;

        if (!alignSuboptionsToBottom && posY + optionsRect.height + EDGE_OFFSET > window.innerHeight) {
            posY = window.innerHeight - optionsRect.height - EDGE_OFFSET;
        } else if (alignSuboptionsToBottom && parentOptionRect.bottom - optionsRect.height < EDGE_OFFSET) {
            posY = EDGE_OFFSET;
        }

        optionsRef.current.style.transform = `translate3d(${posX}px, ${posY}px, 0)`;
    }

    function handleBackdropClick(ev: Event) {
        if (onClose) {
            ev.stopPropagation();
            onClose();
        }
    }

    function handleDocumentKeyDown(ev: KeyboardEvent) {
        if (!disableClosingWithESC && onClose && ev.key === Keyboard.Key.Escape) {
            ev.stopPropagation();
            onClose();
        }
    }

    function handleClick(ev: Event) {
        ev.stopPropagation();
        onClick?.();
    }

    useEffect(() => {
        document.addEventListener("keydown", handleDocumentKeyDown, true);

        return () => {
            document.removeEventListener("keydown", handleDocumentKeyDown, true);
        };
    }, []);

    useLayoutEffect(() => {
        positionOptions();
    });

    const dropdownOptions = (
        <div
            class={classname(styles.dropdownOptions, cssClass, { [styles.subOptions]: subOptions })}
            onClick={handleClick}
            ref={optionsRef}>
            {children}
        </div>
    );

    if (subOptions) {
        return dropdownOptions;
    }

    return (
        <div class="dropdownOptionsBackdrop" onClick={handleBackdropClick}>
            {dropdownOptions}
        </div>
    );
}

export { OptionsProps };
export default DropdownOptions;
